import React, { useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Helmet } from "react-helmet-async";
import ShapeOverlayMenu from "../components/Navbar/ShapeOverlayMenu";
import Footer from "../components/Footer/Footer";
import { projects } from "../data/projects";

const PRIMARY = "var(--home-primary)";
const ACCENT  = "var(--home-accent)";
const CTA     = "var(--home-cta)";
const BG      = "var(--home-bg)";
const EASE    = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

function Block({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE, delay } } }}
    >
      {children}
    </motion.div>
  );
}

function SectionLabel({ children }) {
  return (
    <p style={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase",
      color: PRIMARY, opacity: 0.7, marginBottom: 14, marginTop: 0,
    }}>
      {children}
    </p>
  );
}

function Divider() {
  return <div style={{ height: 1, background: PRIMARY, opacity: 0.12, margin: "56px 0" }} />;
}

export default function CaseStudy() {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);
  if (!project) return <Navigate to="/work" replace />;

  const { caseStudy } = project;

  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: "'General Sans', system-ui, sans-serif" }}>
      <Helmet>
        <title>{project.name} — Shreeram Ganeshan</title>
        <meta name="description" content={project.description} />
        <meta property="og:title" content={`${project.name} — Shreeram Ganeshan`} />
        <meta property="og:description" content={project.tagline} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://shreeram.dev/work/${project.slug}`} />
        {project.image && <meta property="og:image" content={`https://shreeram.dev${project.image}`} />}
      </Helmet>
      <ShapeOverlayMenu />

      {/* ── Hero image ── */}
      {project.image ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          style={{ width: "100%", height: 520, overflow: "hidden" }}
        >
          <img
            src={project.image}
            alt={project.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center center", display: "block" }}
          />
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          style={{
            width: "100%", height: 280,
            background: project.placeholder,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <span style={{
            fontFamily: "'Clash Display', sans-serif",
            fontSize: 96, fontWeight: 700, opacity: 0.15,
            color: project.accentColor, letterSpacing: "-0.04em",
          }}>
            {project.name.split(" ").map((w) => w[0]).join("")}
          </span>
        </motion.div>
      )}

      {/* ── Content ── */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "64px 24px 120px" }}>

        {/* Back */}
        <motion.div
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          style={{ marginBottom: 48 }}
        >
          <Link
            to="/work"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
              color: PRIMARY, textDecoration: "none",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M11 7H3M3 7L7 3M3 7L7 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            All Work
          </Link>
        </motion.div>

        {/* Title block */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
          style={{ marginBottom: 32 }}
        >
          <p style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase",
            color: PRIMARY, opacity: 0.7, marginBottom: 12,
          }}>
            {project.category}
          </p>
          <h1 style={{
            fontFamily: "'Clash Display', sans-serif",
            fontSize: "clamp(40px, 7vw, 72px)",
            fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.0,
            color: PRIMARY, margin: "0 0 20px",
          }}>
            {project.name}
          </h1>
          <p style={{
            fontSize: 17, lineHeight: 1.7,
            color: PRIMARY, opacity: 0.7, margin: 0,
          }}>
            {project.description}
          </p>
        </motion.div>

        {/* Meta row */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE, delay: 0.25 }}
          style={{
            display: "flex", flexWrap: "wrap", gap: 10,
            alignItems: "center", marginBottom: 48,
          }}
        >
          <MetaPill label="Role" value={project.role} />
          <MetaPill label="Year" value={project.year} />
          <a
            href={project.link}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase",
              padding: "6px 14px", borderRadius: 999,
              background: PRIMARY, color: BG,
              textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            Visit Site
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ transform: "rotate(-45deg)" }}>
              <path d="M2 10L10 2M10 2H5M10 2V7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </motion.div>

        {/* Tags */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 64 }}
        >
          {project.tags.map((t) => (
            <span key={t} style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase",
              padding: "4px 10px", borderRadius: 999,
              border: `1px solid ${ACCENT}`, color: PRIMARY, opacity: 0.85,
            }}>
              {t}
            </span>
          ))}
        </motion.div>

        <Divider />

        {/* Overview */}
        <Block>
          <SectionLabel>Overview</SectionLabel>
          <p style={{ fontSize: 18, lineHeight: 1.8, color: PRIMARY, margin: 0, fontWeight: 400 }}>
            {caseStudy.overview}
          </p>
        </Block>

        <Divider />

        {/* Challenge */}
        <Block>
          <SectionLabel>The Challenge</SectionLabel>
          <p style={{ fontSize: 16, lineHeight: 1.85, color: PRIMARY, opacity: 0.82, margin: 0 }}>
            {caseStudy.challenge}
          </p>
        </Block>

        <Divider />

        {/* Solution */}
        <Block>
          <SectionLabel>The Solution</SectionLabel>
          <p style={{ fontSize: 16, lineHeight: 1.85, color: PRIMARY, opacity: 0.82, margin: 0 }}>
            {caseStudy.solution}
          </p>
        </Block>

        <Divider />

        {/* Outcome */}
        <Block>
          <SectionLabel>Outcome</SectionLabel>
          <p style={{ fontSize: 18, lineHeight: 1.8, color: PRIMARY, margin: 0, fontWeight: 400 }}>
            {caseStudy.outcome}
          </p>
        </Block>

        {caseStudy.responsibilities?.length > 0 && (
          <>
            <Divider />
            <Block>
              <SectionLabel>What I Did</SectionLabel>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
                {caseStudy.responsibilities.map((item, i) => (
                  <li key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <span style={{
                      marginTop: 8, flexShrink: 0,
                      width: 5, height: 5, borderRadius: "50%",
                      background: PRIMARY,
                    }} />
                    <span style={{ fontSize: 15, lineHeight: 1.75, color: PRIMARY, opacity: 0.8 }}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </Block>
          </>
        )}
      </div>

      {/* Next project */}
      <NextProject current={slug} />

      <Footer />
    </div>
  );
}

function MetaPill({ label, value }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "5px 12px", borderRadius: 999,
      border: `1px solid ${PRIMARY}`, opacity: 0.5,
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 10, letterSpacing: "0.12em",
      color: PRIMARY,
    }}>
      <span style={{ opacity: 0.55, textTransform: "uppercase" }}>{label}</span>
      <span style={{ opacity: 0.9 }}>{value}</span>
    </span>
  );
}

function NextProject({ current }) {
  const idx  = projects.findIndex((p) => p.slug === current);
  const next = projects[(idx + 1) % projects.length];
  const ref  = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <div ref={ref} style={{ padding: "0 24px 80px", maxWidth: 720, margin: "0 auto" }}>
      <motion.div variants={fadeUp} initial="hidden" animate={inView ? "visible" : "hidden"}>
        <p style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase",
          color: PRIMARY, opacity: 0.6, marginBottom: 16,
        }}>
          Next Project
        </p>
        <Link
          to={`/work/${next.slug}`}
          style={{ textDecoration: "none" }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <motion.div
            whileHover={{ backgroundColor: "var(--home-cta-dark)" }}
            transition={{ duration: 0.25 }}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 16, padding: "32px 36px",
              background: PRIMARY, borderRadius: 20,
            }}
          >
            <div>
              <h2 style={{
                fontFamily: "'Clash Display', sans-serif",
                fontSize: "clamp(28px, 5vw, 52px)",
                fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.0,
                color: BG, margin: "0 0 8px",
              }}>
                {next.name}
              </h2>
              <p style={{ fontSize: 13, color: BG, opacity: 0.55, margin: 0 }}>
                {next.tagline}
              </p>
            </div>
            <div style={{
              width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
              border: `1px solid color-mix(in srgb, ${BG} 30%, transparent)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: BG,
            }}>
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none" style={{ transform: "rotate(-45deg)" }}>
                <path d="M3.5 14.5L14.5 3.5M14.5 3.5H7M14.5 3.5V11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </motion.div>
        </Link>
      </motion.div>
    </div>
  );
}
